import React, { useEffect, useState } from "react";
import { refreshToken } from "./utils/auth";
import { Menu, User, X, MessageCircle } from "lucide-react";
import "./styles/Leagues.css";
import ProfileCard from "./components/Profile";
import TopUpModal from "./components/TopUpModal";

function Leagues() {
  const backendURL = process.env.REACT_APP_API_URL;
  const [gameweek, setGameweek] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [pointsMap, setPointsMap] = useState({});
  const [selectedAmount, setSelectedAmount] = useState(50);
  const currentFplId = localStorage.getItem("fplId");
  const [menuOpen, setMenuOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showTopUp, setShowTopUp] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [fetchError, setFetchError] = useState(null);

  // Fetch current gameweek
  useEffect(() => {
    async function fetchGameweek() {
      try {
        const res = await fetch(`${backendURL}/current-gameweek`);
        if (!res.ok) throw new Error("Failed to fetch gameweek");
        const data = await res.json();
        setGameweek(data.currentGameweek ?? null);
      } catch (err) {
        console.error("Error fetching gameweek:", err);
      }
    }
    fetchGameweek();
  }, [backendURL]);

  // Fetch wallet balance displayed in profile component
  useEffect(() => {
    async function fetchWallet() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        let res = await fetch(`${backendURL}/api/user`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
        });

        if (res.status === 401) {
          const newToken = await refreshToken();

          if (newToken) {
            res = await fetch(`${backendURL}/api/user`, {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${newToken}`,
              },
              credentials: "include",
            });
          }
        }

        const data = await res.json();
        if (data.wallet !== undefined) {
          setWalletBalance(data.wallet);
        }
      } catch (err) {
        console.error("Failed to fetch wallet:", err);
      }
    }
    fetchWallet();
  }, [backendURL]);

  // Fetch leagues for selected gameweek and amount
  useEffect(() => {
    async function fetchLeagues() {
      if (!gameweek) return;
      try {
        const res = await fetch(
          `${backendURL}/api/leagues?gameweek=${gameweek}&entryAmount=${selectedAmount}`
        );
        if (!res.ok) throw new Error("Failed to fetch leagues");
        const data = await res.json();

        // Find the user's league
        const userLeague = data.find((league) =>
          league.users.some((user) => String(user.fplId) === String(currentFplId))
        );

        // Filter out the user's league from the rest
        const otherLeagues = data.filter(
          (league) =>
            !league.users.some((user) => String(user.fplId) === String(currentFplId))
        );

        // Create a new array with the user's league first
        const sortedLeagues = userLeague ? [userLeague, ...otherLeagues] : data;
        setLeagues(sortedLeagues);
      } catch (err) {
        console.error("Error fetching leagues:", err);
      }
    }
    fetchLeagues();
  }, [backendURL, gameweek, selectedAmount, currentFplId]);

  // Flatten all users from all leagues for points fetching
  const allUsers = leagues.flatMap((league) => league.users);

  // Fetch points for all users in a single batched request
  useEffect(() => {
    if (fetchError) return;

    async function fetchPoints() {
      if (allUsers.length === 0) return;
      try {
        const fplIds = allUsers.map((u) => u.fplId);
        const res = await fetch(`${backendURL}/api/points/batch`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ fplIds }),
        });
        if (!res.ok) throw new Error("Failed to fetch batch points");
        const data = await res.json();

        const newPointsMap = {};
        Object.entries(data.points).forEach(([fplId, p]) => {
          newPointsMap[fplId] = {
            points: p.points ?? 0,
            benchPoints: p.points_on_bench ?? 0,
            chipUsed: p.chipUsed ?? false,
          };
        });
        setPointsMap(newPointsMap);
      } catch (err) {
        console.error(err.message);
        setFetchError(err.message);
      }
    }
    fetchPoints();
  }, [allUsers, backendURL, fetchError]);

  const handleInstallApp = async () => {
  const deferredPrompt = window.deferredInstallPrompt;

    if (!deferredPrompt) {
      alert("App installation is not available right now.");
      return;
    }

    deferredPrompt.prompt();

    const { outcome } = await deferredPrompt.userChoice;

    console.log(`Install prompt outcome: ${outcome}`);

    window.deferredInstallPrompt = null;
  };

  const handleLogout = async () => {
    if (window.confirm("Are you sure you want to log out?")) {
      try {
        const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
        // Call backend logout to clear refresh token
        await fetch(`${API_URL}/logout`, {
          method: "POST",
          credentials: "include", // Important: send cookies
        });

        localStorage.removeItem("username");
        localStorage.removeItem("email");
        localStorage.removeItem("fplId");
        localStorage.removeItem("token");

        // Redirect to homepage or login
        window.location.href = "/";
      } catch (err) {
        console.error("Logout error:", err);
        // Even if backend fails, still clear frontend data
        localStorage.clear();
        window.location.href = "/";
      }
    }
  };

  return (
    <div className="leagues-page">
      <div className="side-bar">
        <div className="hamburger-menu" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? (
            <X className="icon-hamburger-menu" size={40} />
          ) : (
            <Menu className="icon-hamburger-menu" size={40} />
          )}
        </div>
        <div className="profile" onClick={() => setShowProfile(true)}>
          <User className="icon-profile" size={40} />
        </div>
        <div className="logout">
          <button className="logout-button" onClick={handleLogout}>
            Log out
          </button>
        </div>
      </div>

      {/* Hamburger Menu */}
        {menuOpen && (
          <div className="hamburger-dropdown">
            <div className="menu-header">
              <span>FPL365</span>
              <small>Menu</small>
            </div>

            <div className="menu-items">
              <a href="/contact" className="menu-item">
                <div className="menu-item-icon">
                  💬
                </div>
                <div className="menu-item-text">
                  <strong>Contact Us</strong>
                  <span>Get in touch with us</span>
                </div>
              </a>

              <a
                href="https://chat.whatsapp.com/ILssAdPpUi4E066RBsaGtf"
                target="_blank"
                rel="noopener noreferrer"
                className="menu-item"
              >
                <div className="menu-item-icon">
                  <MessageCircle size={20} />
                </div>
                <div className="menu-item-text">
                  <strong>WhatsApp Group</strong>
                  <span>Join the FPL365 community</span>
                </div>
              </a>

              <button className="menu-item install-app-item"
                      onClick={handleInstallApp}
              >
                <div className="menu-item-icon">
                  📱
                </div>
                <div className="menu-item-text">
                  <strong>Install App</strong>
                  <span>Install FPL365 on your device</span>
                </div>
              </button>
            </div>
          </div>
        )}

      <div className="main-page">
        <div className="header">
          <p className="click-name-note">
            You can view your opponent's team by clicking on their username.
          </p>
          <h1>Leagues - {selectedAmount} Shs</h1>
          <div className="amount-buttons">
            {[50, 100, 250].map((amt) => (
              <button
                key={amt}
                onClick={() => setSelectedAmount(amt)}
                className={`amount-button ${selectedAmount === amt ? "active" : ""}`}
              >
                {`${amt} Shs Entries`}
              </button>
            ))}
          </div>
        </div>

        <div className="leagues-containers">
          {leagues.length === 0 ? (
            <p className="empty-league-message">No players in this group yet.</p>
          ) : (
            leagues.map((league, index) => {
              const sortedLeaguePlayers = [...league.users].sort((a, b) => {
                const aData = pointsMap[a.fplId] || { points: 0, benchPoints: 0, chipUsed: false };
                const bData = pointsMap[b.fplId] || { points: 0, benchPoints: 0, chipUsed: false };

                // Chip-used players always sink to the bottom, regardless of points
                if (aData.chipUsed && !bData.chipUsed) return 1;
                if (!aData.chipUsed && bData.chipUsed) return -1;

                if (bData.points !== aData.points) return bData.points - aData.points;
                return bData.benchPoints - aData.benchPoints;
              });

              return (
                <div key={league._id} className="leagues-card">
                  <h2>
                    Group #{index + 1}
                    {league.users.some(
                      (user) => String(user.fplId) === String(currentFplId)
                    ) && " (Your Group)"}
                  </h2>
                  <div className="table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          <th>Rank</th>
                          <th>Username</th>
                          <th>Points</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedLeaguePlayers.map((player, i) => (
                          <tr
                            key={player.fplId}
                            className={
                              String(player.fplId) === String(currentFplId)
                                ? "highlight-user-row"
                                : ""
                            }
                          >
                            <td>{i + 1}</td>
                            <td
                              className="clickable-username"
                              onClick={() => {
                                if (gameweek) {
                                  window.open(
                                    `https://fantasy.premierleague.com/entry/${player.fplId}/event/${gameweek}/`,
                                    "_blank"
                                  );
                                }
                              }}
                              style={{ cursor: "pointer", color: "white" }}
                            >
                              {player.username}
                            </td>
                            <td>
                              {pointsMap[player.fplId]?.chipUsed ? (
                                <span style={{ color: "orange", fontWeight: 600 }}>
                                  Chip Used
                                </span>
                              ) : (
                                <>
                                  {pointsMap[player.fplId]?.points ?? "Loading..."}
                                  {sortedLeaguePlayers.some(
                                    (other) =>
                                      other.fplId !== player.fplId &&
                                      pointsMap[other.fplId]?.points ===
                                        pointsMap[player.fplId]?.points
                                  ) && (
                                    <span
                                      style={{
                                        fontSize: "0.75em",
                                        color: "gray",
                                        marginLeft: "4px",
                                      }}
                                    >
                                      ({pointsMap[player.fplId]?.benchPoints} bench)
                                    </span>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showProfile && (
        <ProfileCard
          username={localStorage.getItem("username") || "Unknown"}
          walletBalance={walletBalance}
          setWalletBalance={setWalletBalance}
          onTopUp={() => setShowTopUp(true)}
          onClose={() => setShowProfile(false)}
        />
      )}
      {showTopUp && (
        <TopUpModal
          onClose={() => setShowTopUp(false)}
          onSuccess={(newBalance) => {
            setWalletBalance(newBalance);
            setShowTopUp(false);
          }}
        />
      )}
    </div>
  );
}

export default Leagues;